import { useSelector } from "react-redux";
import { initialStateWork } from "../../store/jsonSlice"; 
import { initialStateLearn } from "../../store/jsonSliceLearn";
import { normalizeHologramState, normalizeHologramStatesInJson } from "../../utils/hologramState";

/**
 * Ne garde que les clés connues d'une question pour l'export JSON.
 * Toute nouvelle clé de question doit être ajoutée ici.
 */
export function cleanQuizForExport(quiz) {
  let finalOrdered = quiz.ordered !== undefined ? quiz.ordered : false;
  if (!finalOrdered && Array.isArray(quiz.quizChoices)) {
    const foundOrdered = quiz.quizChoices.find(c => c.ordered === true);
    if (foundOrdered) finalOrdered = true;
  }

  const cleanedChoices = (quiz.quizChoices || []).map(({ ordered, ...choiceRest }) => choiceRest);

  return {
    quizQuestion: quiz.quizQuestion,
    quizNote: quiz.quizNote,
    quizTextAudio: quiz.quizTextAudio || "",
    quizImg: quiz.quizImg || "",
    quizHologram: quiz.quizHologram || "",
    // Anciens JSON sans cette clé : "fixe" par défaut
    quizHologramInteractable: quiz.quizHologramInteractable === true,
    // Anciens JSON sans cette clé : { blinkShape: "Off", blinkSpeed: 1 }
    quizHologramState: normalizeHologramState(quiz.quizHologramState),
    quizAudio: quiz.quizAudio || "",
    quizChoices: cleanedChoices,
    quizIndicators: quiz.quizIndicators || [],
    ordered: finalOrdered
  };
}

/*** Complète `currentObj` avec les valeurs par défaut de `defaultObj` (les tableaux sont conservés tels quels) */
export function mergeWithDefaults(defaultObj, currentObj) {
  if (Array.isArray(defaultObj)) {
    return Array.isArray(currentObj) ? currentObj : [];
  } else if (typeof defaultObj === 'object' && defaultObj !== null) {
    const merged = { ...defaultObj };
    for (const key in defaultObj) {
      if (Object.prototype.hasOwnProperty.call(currentObj || {}, key)) {
        merged[key] = mergeWithDefaults(defaultObj[key], currentObj[key]);
      }
    }
    for (const key in currentObj) {
      if (!Object.prototype.hasOwnProperty.call(defaultObj, key)) {
        merged[key] = currentObj[key];
      }
    }
    return merged;
  } else {
    return currentObj !== undefined ? currentObj : defaultObj;
  }
}

/**
 * Construit le JSON exporté : valeurs par défaut, questions nettoyées, puis normalisation de
 * tous les sous-objets <préfixe>HologramState (topics, questions, steps, actions, process),
 * même pour un ancien JSON qui n'en contient pas.
 */
export function buildExportJson(jsonData, defaultJsonData) {
  const completedJson = mergeWithDefaults(defaultJsonData, jsonData);

  let cleanedJson = { ...completedJson };
  if (cleanedJson.process && Array.isArray(cleanedJson.process.topics)) {
    cleanedJson.process = {
      ...cleanedJson.process,
      topics: cleanedJson.process.topics.map(topic => {
        if (Array.isArray(topic.quizzes)) {
          return {
            ...topic,
            quizzes: topic.quizzes.map(cleanQuizForExport)
          };
        }
        return topic;
      })
    };
  }

  return normalizeHologramStatesInJson(cleanedJson);
}

const ExportJsonButton = ({ type = "learn" }) => {
  // On récupère le jsonData du store Redux selon le type
  const jsonData = useSelector((state) =>
    type === "work"
      ? state.json?.jsonData
      : state.jsonLearn?.jsonData
  );
  // On récupère le nom de langue et le nom du fichier courant depuis le store
  const folderName = useSelector((state) =>
    type === "work"
      ? state.jsonWork?.folderName
      : state.jsonLearn?.folderName
  );
  const filename = useSelector((state) =>
    type === "work"
      ? state.jsonWork?.filename
      : state.jsonLearn?.filename
  );

   // Fonction pour déterminer le nom du fichier exporté
   const getExportFilename = () => {
     const pathSegments = window.location.pathname.split('/');
     
     if (type === "learn") {
       const chapter = pathSegments[2] ? decodeURIComponent(pathSegments[2]) : "";
       const variant = pathSegments[3] ? decodeURIComponent(pathSegments[3]) : "";
       if (chapter && variant) {
         return `${chapter}_${variant}_learn.json`;
       }
     }

     let file = filename || folderName;
     if (!file) {
       file = pathSegments.reverse().find(seg => seg.toLowerCase().endsWith('.json')) || (type === "work" ? "procedure_work_export.json" : "formation_learn_export.json");
     }
     file = file.split('/').pop();
     file = decodeURIComponent(file);
     file = file.replace(/\.json$/, "");
     let lang = localStorage.getItem("I18N_LANGUAGE") || "fr";
     let base = file;
     const match = file.match(/(.+)_([a-zA-Z]{2,3})$/);
     if (match) {
       base = match[1];
       lang = match[2];
     }
     console.log(`Exporting ${type} JSON with base: ${base}, lang: ${lang}`);
     return `${base}.json`;
   }

  // Structures par défaut selon le type

  const defaultJsonData = type === "work" ? initialStateWork.jsonData : initialStateLearn.jsonData;

  const handleExport = () => {
    if (!jsonData) return;
    const cleanedJson = buildExportJson(jsonData, defaultJsonData);

    const dataStr = JSON.stringify(cleanedJson, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = getExportFilename();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <button className="btn btn-sm btn-outline-success mx-2" onClick={handleExport}>
      <i className="dripicons-upload align-middle"></i> Exporter le JSON
    </button>
  );
};

export default ExportJsonButton;


//Creators : Florian Cohen-Joly @3DDGAMES -- Under license